import React, { useEffect, useRef, useState } from 'react';
import './Terms.css';

const TERMS_OVERVIEW = [
  'Flotte es una plataforma SaaS B2B y cada usuario opera con credenciales asignadas por su organización.',
  'El acceso depende de la vigencia de pagos y del apego a las políticas de seguridad y privacidad.',
  'Las empresas conservan la titularidad de su información y autorizan a Flotte a procesarla conforme a estos términos.',
];

const TERMS_ACCEPTABLE_USE = [
  {
    title: 'Credenciales personales',
    description:
      'Cada usuario gestiona su cuenta individual, activa MFA cuando esté disponible y notifica accesos sospechosos en menos de 24 horas.',
  },
  {
    title: 'Contenido permitido',
    description:
      'Solo se publican unidades, rutas y documentos vinculados con operaciones logísticas legales, verificables y vigentes.',
  },
  {
    title: 'Integraciones y API',
    description:
      'Los conectores, API y webhooks se consumen respetando límites publicados sin intentar descifrar ni intervenir componentes del servicio.',
  },
];

const TERMS_RESPONSIBILITIES = [
  'Flotte mantiene infraestructura, seguridad, respaldo y continuidad operativa de la plataforma.',
  'Cada empresa garantiza la veracidad de la información y documentación que comparte o publica.',
  'Ambas partes notifican incidentes de seguridad con un máximo de 24 horas desde su detección.',
];

const TERMS_BILLING = [
  'Las suscripciones se facturan de forma mensual o anual. Moras mayores a 15 días pueden suspender el acceso.',
  'Las cancelaciones requieren aviso escrito con al menos 30 días naturales de anticipación.',
  'Servicios profesionales (onboarding, integraciones o acompañamiento) se cotizan y facturan de manera independiente.',
];

const TERMS_IP = [
  'Flotte conserva la propiedad intelectual de la plataforma, marcas, documentación y modelos utilizados para prestar el servicio.',
  'Los clientes retienen la titularidad de los datos que cargan y garantizan contar con los permisos para compartirlos.',
  'Las sugerencias o mejoras enviadas a Flotte pueden incorporarse al producto sin obligación de compensación adicional.',
];

const TERMS_SLA_TABLE = [
  {
    label: 'Disponibilidad mensual comprometida',
    value: '99.5%',
    detail: 'Calculada sobre ventanas de 60 segundos, excluyendo mantenimientos planificados notificados con 48 h.',
  },
  {
    label: 'Ventana de mantenimiento programado',
    value: 'Domingo 00:00–04:00 (CST)',
    detail: 'Se utiliza únicamente cuando es necesario aplicar cambios de infraestructura o seguridad.',
  },
  {
    label: 'Tiempo medio de respuesta de soporte',
    value: '< 4 h hábiles',
    detail: 'Escalamientos urgentes (incidentes críticos o legales) se atienden a través de legal@flotte.mx.',
  },
];

const TERMS_SECTIONS = [
  {
    id: 'resumen',
    label: 'Resumen ejecutivo',
    title: 'Marco general del servicio',
    kicker: 'Panorama',
    paragraphs: [
      'Estos términos constituyen un contrato entre Flotte y las organizaciones que acceden a la plataforma para gestionar sus operaciones logísticas.',
      'Al crear una cuenta o continuar utilizando Flotte aceptas las obligaciones descritas a continuación, así como los lineamientos de privacidad vigentes.',
    ],
    calloutTitle: 'Puntos esenciales',
    calloutItems: TERMS_OVERVIEW,
  },
  {
    id: 'uso-aceptable',
    label: 'Uso aceptable',
    title: 'Reglas de uso y comportamiento',
    kicker: 'Operación diaria',
    paragraphs: [
      'El acceso a Flotte se otorga de manera individual y debe utilizarse exclusivamente para fines legales relacionados con las operaciones de la empresa que lo otorga.',
      'El incumplimiento de estas reglas puede implicar la suspensión inmediata de usuarios o instancias.',
    ],
    rules: TERMS_ACCEPTABLE_USE,
  },
  {
    id: 'pagos',
    label: 'Pagos y facturación',
    title: 'Condiciones comerciales',
    kicker: 'Modelos comerciales',
    paragraphs: [
      'La suscripción a Flotte se contrata por periodos mensuales o anuales y se renueva de manera automática salvo notificación en sentido contrario.',
    ],
    list: TERMS_BILLING,
    calloutTitle: 'Recordatorios clave',
    calloutItems: [
      'Los cargos se realizan en moneda pactada y se actualizan según la inflación publicada por Banxico o los anexos contractuales.',
      'Los servicios adicionales se cotizan bajo alcance y tarifas aprobadas por escrito.',
    ],
  },
  {
    id: 'propiedad-intelectual',
    label: 'Propiedad intelectual',
    title: 'Derechos sobre datos y software',
    kicker: 'Titularidad',
    paragraphs: [
      'Flotte concede una licencia limitada, revocable y no transferible para utilizar la plataforma durante la vigencia del contrato.',
    ],
    list: TERMS_IP,
  },
  {
    id: 'soporte',
    label: 'Disponibilidad y soporte',
    title: 'SLA y responsabilidades compartidas',
    kicker: 'Continuidad operativa',
    paragraphs: [
      'La continuidad del servicio se garantiza bajo un esquema de disponibilidad y soporte acordado con cada cliente.',
    ],
    calloutTitle: 'Responsabilidades compartidas',
    calloutItems: TERMS_RESPONSIBILITIES,
    table: TERMS_SLA_TABLE,
  },
];

export default function Terms() {
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(TERMS_SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0.25, 0.5, 0.75] }
    );

    Object.values(sectionRefs.current).forEach(node => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const registerSection = id => node => {
    if (node) {
      sectionRefs.current[id] = node;
    }
  };

  return (
    <main className="terms-doc">
      <section className="terms-doc__hero">
        <div className="terms-doc__hero-surface">
          <p className="terms-doc__label">Flotte · Condiciones de servicio</p>
          <h1 className="terms-doc__title">Términos y Condiciones</h1>
          <p className="terms-doc__lede">
            Documento contractual que describe responsabilidades, lineamientos de uso y compromisos de disponibilidad al
            operar la plataforma Flotte.
          </p>
          <ul className="terms-doc__meta">
            <li>Última actualización: Junio 2024</li>
            <li>Versión 2.1</li>
          </ul>
        </div>
      </section>

      <div className="terms-doc__layout">
        <nav className="terms-doc__toc" aria-label="Tabla de contenidos">
          <p className="terms-doc__toc-label">Contenido</p>
          <ul>
            {TERMS_SECTIONS.map(section => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={activeSection === section.id ? 'is-active' : undefined}
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="terms-doc__content">
          {TERMS_SECTIONS.map(section => (
            <section
              key={section.id}
              id={section.id}
              ref={registerSection(section.id)}
              className="terms-doc__section"
            >
              <div className="terms-doc__section-head">
                <span className="terms-doc__section-kicker">{section.kicker}</span>
                <h2>{section.title}</h2>
              </div>

              {section.paragraphs?.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}

              {section.rules && (
                <dl className="terms-doc__rules">
                  {section.rules.map(rule => (
                    <div key={rule.title}>
                      <dt>{rule.title}</dt>
                      <dd>{rule.description}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {section.list && (
                <ul className="terms-doc__list">
                  {section.list.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}

              {section.calloutItems && (
                <aside className="terms-doc__callout">
                  <p className="terms-doc__callout-label">{section.calloutTitle}</p>
                  <ul>
                    {section.calloutItems.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </aside>
              )}

              {section.table && (
                <div className="terms-doc__table-wrapper">
                  <table>
                    <tbody>
                      {section.table.map(row => (
                        <tr key={row.label}>
                          <th scope="row">
                            <span>{row.label}</span>
                            <strong>{row.value}</strong>
                          </th>
                          <td>{row.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>

      <footer className="terms-doc__footer">
        <p>¿Dudas legales? <a href="mailto:legal@flotte.mx">legal@flotte.mx</a></p>
      </footer>
    </main>
  );
}
